import { useState, type FormEvent, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Center } from "@astryxdesign/core/Center";
import { Card } from "@astryxdesign/core/Card";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Divider } from "@astryxdesign/core/Divider";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Theme } from "@astryxdesign/core/theme";
import { madhaniTheme } from "../../theme/madhani";
import { useClockTick } from "../../lib/useClockTick";
import { api } from "../../lib/api";
import { useAuth } from "./AuthProvider";

// Static assets served from public/, not imported — same reasoning as any
// public/ file in Vite: they need no bundling or hashing, just a stable URL.
// The official company lockup (mark + "Construction & Mining Contractors"
// wordmark baked into the raster) — kept only here now that App.tsx's navbar
// uses the app's own icon instead, so this is the one remaining place it
// appears in the frontend.
const MADHANI_LOGO_URL = "/images/madhani-logo.png";
const LOGIN_BACKGROUND_URL = "/images/login-background.jpg";

// A real photo of the operation this product actually watches, not an
// abstract motif standing in for one. `color-mix()` keeps the scrim itself
// a theme token (the same one the plain background used before this photo
// existed) rather than a new hardcoded rgba() — only its opacity is new,
// which two stops need since the card sits mid-image where a single flat
// tint would leave whatever's brightest in the source photo fighting the
// card for attention.
const BACKDROP_STYLE = {
  position: "relative" as const,
  overflow: "hidden" as const,
  backgroundImage:
    "linear-gradient(180deg, color-mix(in srgb, var(--color-background-body) 55%, transparent), color-mix(in srgb, var(--color-background-body) 82%, transparent)), " +
    `url('${LOGIN_BACKGROUND_URL}')`,
  backgroundSize: "cover",
  backgroundPosition: "center",
};

// Three soft accent markers scattered over the photo, like live camera
// positions on the site itself — purely decorative (the wrapping VStack is
// aria-hidden), built from the existing StatusDot primitive rather than new
// keyframes, so the pulsing already respects prefers-reduced-motion the
// same way every other "live" indicator in this app does.
const DECORATIVE_MARKERS: Array<{ top: string; left: string }> = [
  { top: "18%", left: "12%" },
  { top: "70%", left: "82%" },
  { top: "82%", left: "20%" },
];

function LoginClock() {
  const now = useClockTick();
  const label = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(now);
  return (
    <HStack gap={1.5} vAlign="center">
      <StatusDot variant="success" label="Live" isPulsing />
      <Text type="code" color="secondary" hasTabularNumbers>
        {label}
      </Text>
    </HStack>
  );
}

function LoginForm() {
  const { login } = useAuth();
  const location = useLocation();
  // Same "from" location.state RequireAuth sets when it bounces a signed-out
  // visit — see App.tsx's LoginRoute for the post-local-login equivalent of
  // this same redirect-back behaviour.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  // Always enabled, never gated on auth: this is the whole point of a login
  // page — it has to answer before any session exists.
  const { data: oidcStatus, isLoading: isOidcStatusLoading } = useQuery({
    queryKey: ["oidc-status"],
    queryFn: api.auth.oidcStatus,
  });
  // A failed or still-resolving request is treated the same as OIDC being
  // administratively disabled — fail closed to the local form rather than
  // ever collapsing it on the strength of an error.
  const ssoEnabled = oidcStatus?.enabled ?? false;

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // null = "no explicit choice yet, use the computed default below"; once
  // resolved to a boolean it wins regardless of ssoEnabled, so an operator
  // who reveals the local form (or hides it again) is never overridden by
  // this component re-rendering.
  const [localFormOverride, setLocalFormOverride] = useState<boolean | null>(null);
  // Collapsed by default once single sign-on is available: most people here
  // will use it, and defaulting everyone into a password prompt they don't
  // need is exactly what this is meant to avoid. Nobody is locked out of the
  // local form — the toggle below always reveals it, which is what the
  // break-glass admin account (or anyone else while SSO is misconfigured)
  // actually needs.
  const showLocalForm = localFormOverride ?? !ssoEnabled;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setRequestError(null);
    setIsSubmitting(true);
    try {
      await login(usernameOrEmail, password);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Couldn't sign in.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // While the status check is in flight, show a placeholder shaped like
  // either branch below rather than defaulting to one and having it pop or
  // collapse out from under someone already reading (or typing into) it.
  let authControls: ReactNode;
  if (isOidcStatusLoading) {
    authControls = (
      <VStack gap={3}>
        <Skeleton height={44} radius={2} />
        <Skeleton height={44} radius={2} />
      </VStack>
    );
  } else {
    authControls = (
      <VStack gap={3}>
        {ssoEnabled && (
          <Button
            label={`Sign in with ${oidcStatus?.display_name ?? "single sign-on"}`}
            type="button"
            variant="primary"
            size="lg"
            width="100%"
            onClick={() => {
              window.location.href = api.auth.oidcLoginUrl(from);
            }}
          />
        )}

        {ssoEnabled && (
          <Button
            label={showLocalForm ? "Use single sign-on instead" : "Sign in with username and password instead"}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setLocalFormOverride(!showLocalForm)}
          />
        )}

        {showLocalForm && (
          <VStack gap={3}>
            {ssoEnabled && <Divider />}
            <FormLayout>
              <TextInput
                label="Username or email"
                value={usernameOrEmail}
                onChange={setUsernameOrEmail}
                isRequired
                hasAutoFocus={!ssoEnabled}
              />
              <TextInput label="Password" type="password" value={password} onChange={setPassword} isRequired />
              {requestError && <Banner status="error" title="Couldn't sign in" description={requestError} />}
            </FormLayout>

            {/* Secondary once SSO is the primary path — same
                no-two-primary-buttons rule this app already applies
                elsewhere. When SSO is off entirely, this is the only
                control on the page, so it stays primary. */}
            <Button
              label="Sign in"
              type="submit"
              variant={ssoEnabled ? "secondary" : "primary"}
              isLoading={isSubmitting}
            />
          </VStack>
        )}
      </VStack>
    );
  }

  return (
    <Card width={380} elevation="high">
      <form onSubmit={handleSubmit}>
        <VStack gap={5}>
          <VStack gap={2} hAlign="center">
            <img src={MADHANI_LOGO_URL} alt="PT Madhani Talatah Nusantara — Construction & Mining Contractors" width={220} height={35} />
            <VStack gap={0.5} hAlign="center">
              <Heading level={1} type="display-2">
                Madhani CCTV
              </Heading>
              <Text color="secondary" type="supporting" justify="center">
                Sign in to view live cameras
              </Text>
            </VStack>
          </VStack>

          {authControls}

          <LoginClock />
        </VStack>
      </form>
    </Card>
  );
}

// The one screen in this app where bespoke composition (beyond component
// props) is justified — same precedent as tileChrome.ts's documented
// exception for video-chrome legibility. Forced to dark mode regardless of
// the signed-out viewer's OS/app preference: a login screen is a good place
// for one fixed, deliberate mood, and this is the first thing a third party
// evaluating the product sees. Every color used is still a theme token, not
// a new hardcoded value — nesting a second <Theme mode="dark"> just inverts
// which side of each token's light-dark() pair resolves for this subtree.
export function LoginPage() {
  return (
    <Theme theme={madhaniTheme} mode="dark">
      <Center height="100dvh" padding={4} style={BACKDROP_STYLE}>
        <VStack aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
          {DECORATIVE_MARKERS.map((pos) => (
            <VStack key={`${pos.top}-${pos.left}`} style={{ position: "absolute", top: pos.top, left: pos.left }}>
              <StatusDot variant="success" label="" isPulsing />
            </VStack>
          ))}
        </VStack>

        <VStack gap={4} hAlign="center" style={{ position: "relative" }}>
          <LoginForm />
          <Text type="supporting" color="secondary">
            © {new Date().getFullYear()} PT Madhani Talatah Nusantara. All rights reserved.
          </Text>
        </VStack>
      </Center>
    </Theme>
  );
}
